<template>
  <box-default>
    <div class="col-xs-12">
      <div class="default-box pi-page pi-ai">
        <h1 class="pi-page__title">Product Intelligence</h1>
        <pi-tabs current="assistant"/>
        <pi-assistant-nav current="chat"/>

        <pi-data-state :loading="agentsRequest.loading" :error="agentsRequest.error" :envelope="agentsRequest.envelope" :show-footer="false"
                       loading-text="Carregando o assistente…" @retry="loadAgents(true)">
          <details class="pi-ai__guide" :open="!turns.length && !pending">
            <summary class="pi-ai__guide-title">Como usar o assistente</summary>
            <ul class="pi-ai__guide-list">
              <li v-for="(item, index) in guide" :key="index">{{ item }}</li>
            </ul>
          </details>

          <div v-if="!configured" class="pi-ai__notice" role="status">
            <strong class="pi-ai__notice-title">Assistente não configurado</strong>
            <span>{{ notConfiguredText }}</span>
            <span>O histórico desta sessão e o Consumo da IA continuam disponíveis.</span>
          </div>

          <div class="pi-ai__toolbar">
            <div class="pi-toolbar__field pi-ai__agent">
              <span class="pi-ai__agent-label"><label class="pi-toolbar__text" :for="ids.agent">Agente</label><pi-definition-tooltip class="pi-ai__info" info-key="assistant.agent" label="Agente"/></span>
              <select :id="ids.agent" v-model="agent" class="form-control pi-input" :disabled="asking" :aria-describedby="ids.agentHelp">
                <optgroup v-for="group in agentGroups" :key="group.key" :label="group.label">
                  <option v-for="option in group.agents" :key="option.key" :value="option.key">{{ option.label }}</option>
                </optgroup>
              </select>
            </div>
            <button type="button" class="pi-btn pi-btn--ghost" :disabled="asking || (!turns.length && !askError)" @click="newConversation">Nova conversa</button>
            <div v-if="contextText" class="pi-ai__context">
              <span class="pi-ai__context-text">{{ contextText }}</span>
              <button type="button" class="pi-ai__context-remove" :aria-label="'Remover o contexto da pergunta (' + contextText + ')'" :disabled="asking"
                      @click="removeContext">×</button>
            </div>
          </div>
          <div :id="ids.agentHelp" class="pi-ai__agent-help">
            <p v-if="agentHelp.expertise" class="pi-ai__expertise"><strong>Especialista em:</strong> {{ agentHelp.expertise }}</p>
            <p v-if="agentHelp.description" class="pi-muted pi-ai__agent-description">{{ agentHelp.description }}</p>
          </div>

          <p class="pi-ai__cost">{{ costNotice }}<pi-definition-tooltip class="pi-ai__info" info-key="assistant.costEstimate" label="Estimativa de custo"/></p>
          <p class="pi-muted pi-ai__today">{{ usageToday }}<pi-definition-tooltip class="pi-ai__info" info-key="assistant.todayUsage" label="Uso de hoje"/></p>
          <p v-if="ceilingReached" class="pi-warn" role="status">Teto diário de custo atingido para todos: novas perguntas voltam amanhã.</p>

          <div v-if="suggestions.length" class="pi-ai__suggestions" role="group" :aria-label="'Perguntas sugeridas para ' + agentName">
            <button v-for="(question, index) in suggestions" :key="index" type="button" class="pi-btn pi-btn--ghost pi-ai__suggestion"
                    :disabled="sendBlocked" @click="ask(question)">{{ question }}</button>
          </div>

          <section v-if="areaQuestions.length" class="pi-ai__areas" aria-labelledby="pi-ai-areas-title">
            <h2 id="pi-ai-areas-title" class="pi-ai__areas-title">
              Perguntas por área do produto<pi-definition-tooltip class="pi-ai__info" info-key="assistant.areaAgents" label="Áreas do produto"/>
            </h2>
            <div v-for="area in areaQuestions" :key="area.key" class="pi-ai__area" role="group" :aria-label="'Perguntas sugeridas para ' + area.label">
              <span class="pi-ai__area-label">{{ area.label }}</span>
              <button v-for="(question, index) in area.questions" :key="index" type="button" class="pi-btn pi-btn--ghost pi-ai__suggestion"
                      :disabled="sendBlocked" @click="askAs(area.key, question)">{{ question }}</button>
            </div>
          </section>

          <section class="pi-ai__conversation" aria-labelledby="pi-ai-conversation-title">
            <h2 id="pi-ai-conversation-title" class="pi-sr">Conversa</h2>
            <p v-if="hiddenTurnsText" class="pi-muted">{{ hiddenTurnsText }}</p>
            <p v-if="restartNote && !turns.length && !pending" class="pi-muted">{{ restartNote }}</p>
            <article v-for="turn in turns" :key="turn.key" class="pi-ai__turn" :class="{ 'is-flash': flash.anchor === turn.anchor && !flash.link }"
                     :data-turn="turn.anchor" :aria-label="'Pergunta ' + (turn.index + 1)">
              <div class="pi-ai__question">
                <span class="pi-sr">Você perguntou: </span>{{ turn.question }}
              </div>
              <div class="pi-ai__answer">
                <pi-assistant-answer :body="turn.answer.body" :status="turn.answer.statusInfo" :show-status="turn.answer.showStatus"
                                     :links="turn.answer.links" :warnings="turn.answer.warnings" :interactive="true" :busy="sendBlocked"
                                     :clarification="turn.answer.status === 'needs_clarification'" :link-query="turn.linkQuery"
                                     :visited="turn.visited" :flash="flash.anchor === turn.anchor ? flash.link : ''"
                                     :export-context="turn.exportContext" @follow-up="ask" @open-link="openLink(turn, $event)"/>
                <pi-assistant-trace :steps="turn.answer.trace"/>
                <p class="pi-ai__footer">
                  <span>{{ turn.answer.routeText }}</span>
                  <span>{{ turn.answer.usage.calls }}</span>
                  <span>{{ turn.answer.usage.tokens }}</span>
                  <span>{{ turn.answer.usage.cost }}</span>
                  <span>{{ turn.answer.usage.time }}</span>
                  <pi-definition-tooltip info-key="assistant.answerUsage" label="Consumo da resposta"/>
                  <router-link v-if="turn.usageRoute" class="pi-link pi-ai__footer-link" :to="turn.usageRoute">Ver entrada e saída</router-link>
                </p>

                <div v-if="turn.answer.interactionId" class="pi-ai__feedback">
                  <span :id="'pi-ai-fb-' + turn.answer.interactionId" class="pi-ai__feedback-label">{{ turn.feedback ? 'Sua avaliação:' : 'Esta resposta foi útil?' }}</span>
                  <div class="pi-ai__feedback-choices" role="group" :aria-labelledby="'pi-ai-fb-' + turn.answer.interactionId">
                    <button type="button" class="pi-toggle__btn pi-ai__feedback-btn" :class="{ 'is-on': turn.feedbackValue === 'useful' }"
                            :aria-pressed="turn.feedbackValue === 'useful' ? 'true' : 'false'" :disabled="feedbackOf(turn).busy"
                            @click="vote(turn, 'useful')">Útil</button>
                    <button type="button" class="pi-toggle__btn pi-ai__feedback-btn" :class="{ 'is-on': turn.feedbackValue === 'not_useful' }"
                            :aria-pressed="turn.feedbackValue === 'not_useful' ? 'true' : 'false'" :disabled="feedbackOf(turn).busy"
                            @click="vote(turn, 'not_useful')">Não útil</button>
                  </div>
                  <span v-if="feedbackOf(turn).busy && !feedbackOf(turn).commentOpen" class="pi-muted">Registrando…</span>
                  <template v-if="turn.feedback && !feedbackOf(turn).commentOpen">
                    <span v-if="turn.feedback.comment" class="pi-ai__feedback-comment">“{{ turn.feedback.comment }}”</span>
                    <button type="button" class="pi-ai__feedback-edit" :disabled="feedbackOf(turn).busy" @click="openComment(turn)">
                      {{ turn.feedback.comment ? 'Editar comentário' : 'Adicionar comentário' }}
                    </button>
                  </template>
                  <form v-if="turn.feedback && feedbackOf(turn).commentOpen" class="pi-ai__feedback-form" @submit.prevent="sendComment(turn)">
                    <label class="pi-ai__feedback-field" :for="'pi-ai-fbc-' + turn.answer.interactionId">
                      <span>Comentário (opcional)</span>
                      <textarea :id="'pi-ai-fbc-' + turn.answer.interactionId" class="form-control pi-input pi-ai__feedback-box" rows="2"
                                :maxlength="commentMax" :value="feedbackOf(turn).comment" :disabled="feedbackOf(turn).busy"
                                @input="setFeedbackComment(turn, $event.target.value)"></textarea>
                    </label>
                    <div class="pi-ai__feedback-actions">
                      <button type="submit" class="pi-btn" :disabled="feedbackOf(turn).busy">{{ feedbackOf(turn).busy ? 'Enviando…' : 'Enviar comentário' }}</button>
                      <button type="button" class="pi-btn pi-btn--ghost" :disabled="feedbackOf(turn).busy" @click="closeComment(turn)">Fechar</button>
                    </div>
                  </form>
                  <p v-if="feedbackOf(turn).error" class="pi-ai__feedback-error" role="alert">{{ feedbackOf(turn).error }}</p>
                </div>
                <p v-else class="pi-muted pi-ai__unsaved">Esta pergunta não foi gravada no Consumo da IA; por isso não dá para avaliá-la.</p>
              </div>
            </article>

            <article v-if="pending" class="pi-ai__turn is-pending" aria-label="Pergunta em andamento">
              <div class="pi-ai__question"><span class="pi-sr">Você perguntou: </span>{{ pending.question }}</div>
              <div class="pi-ai__answer pi-ai__waiting">
                <span class="pi-ai__dots" aria-hidden="true"><span></span><span></span><span></span></span>
                <span>{{ pendingText }}</span>
              </div>
            </article>
          </section>

          <p class="pi-sr" role="status" aria-live="polite">{{ announcement }}</p>

          <div v-if="askError" class="pi-ai__error" :class="'pi-ai__error--' + askError.kind" role="alert">
            <span>{{ askError.message }}</span>
            <span v-if="cooldownSeconds" class="pi-ai__countdown">Envio liberado em {{ countdown }}</span>
            <button v-if="askError.retryable && draftReady" type="button" class="pi-btn pi-btn--ghost" :disabled="sendBlocked" @click="submit">Tentar de novo</button>
          </div>

          <form class="pi-ai__input" @submit.prevent="submit">
            <label class="pi-ai__input-label" :for="ids.input">Sua pergunta</label>
            <textarea :id="ids.input" ref="input" v-model="draft" class="form-control pi-ai__input-box" rows="3" :maxlength="maxLength"
                      :disabled="inputDisabled" :placeholder="placeholder" :aria-describedby="ids.hint + ' ' + ids.counter"
                      @keydown="onKeydown"></textarea>
            <div class="pi-ai__input-bar">
              <span :id="ids.hint" class="pi-muted">{{ inputHint }}</span>
              <span :id="ids.counter" class="pi-muted pi-ai__counter">{{ counterText }}</span>
              <button type="submit" class="pi-btn" :disabled="sendBlocked || !draftReady">{{ submitLabel }}</button>
            </div>
          </form>
        </pi-data-state>

        <pi-page-footer :envelopes="lastEnvelopes" :labels="envelopeLabels"/>
      </div>
    </div>
  </box-default>
</template>

<script>
import boxDefault from "../../components/layouts/boxDefault.vue";
import piTabs from "../../components/pi/piTabs.vue";
import piDataState from "../../components/pi/piDataState.vue";
import piPageFooter from "../../components/pi/piPageFooter.vue";
import piDefinitionTooltip from "../../components/pi/piDefinitionTooltip.vue";
import piAssistantNav from "../../components/pi/piAssistantNav.vue";
import piAssistantAnswer from "../../components/pi/piAssistantAnswer.vue";
import piAssistantTrace from "../../components/pi/piAssistantTrace.vue";
import { askAssistant, loadAssistantAgents, loadFeatureOptions, searchCompanies, sendAssistantFeedback } from "../../components/pi/piApi.js";
import { blankRequest, payloadOf, runRequest, shouldLoad } from "../../components/pi/piRequests.js";
import { cachedAgents, chatState, keepAgents, saveChatState, updateChatState } from "../../components/pi/piChatSession.js";
import {
  ASSISTANT_GUIDE, ASSISTANT_LIMITS, CHAT_FLASH_MS, DEFAULT_AGENT, NEW_CHAT_FOR_PREFILL_TEXT, NOT_CONFIGURED_TEXT, PENDING_TEXT, agentByKey,
  agentGroups, agentHelp, agentsLoadError, answerExportContext, appendTurn, applyUsage, areaSuggestions, assistantError, buildAskBody, chatBack,
  chatLinkQuery, chatOrigin, chatPrefill, chatTarget, chatTurnAnchor, chatTurnNumber, cleanQuestion, companyNameFrom, contextCompanyName,
  contextFromRoute, contextLabel, costNoticeText, countdownText, defaultAgentKey, feedbackError, feedbackFromResult, hasChatPrefill, isChatReturn,
  normalizeAgents, normalizeAnswer, prefillStartsNewChat, questionError, restartChat, setTurnFeedback, usageDetailRoute, usageTodayText, visitedKey,
  visitedLinks, withoutChatPrefill, withoutChatQuery, withVisited,
} from "../../components/pi/piAssistantModel.js";

const BLANK_FEEDBACK = { comment: "", busy: false, error: "", commentOpen: false };
const LIMIT_KINDS = ["rate_limited", "daily_budget"];
const DRAFT_DELAY_MS = 400;
const NO_FLASH = { anchor: "", link: "" };
const inflight = {};

const pageScroll = () => {
  try {
    return Math.max(0, Math.round(window.pageYOffset || document.documentElement.scrollTop || 0));
  } catch (e) {
    return 0;
  }
};

export default {
  name: "pi-assistant",
  components: { boxDefault, piTabs, piDataState, piPageFooter, piDefinitionTooltip, piAssistantNav, piAssistantAnswer, piAssistantTrace },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.origin = from && typeof from.path === "string" ? from.path : "";
    });
  },
  beforeRouteLeave(to, from, next) {
    this.saveScroll();
    next();
  },
  data() {
    const session = chatState();
    return {
      ids: { agent: "pi-ai-agent", agentHelp: "pi-ai-agent-help", input: "pi-ai-input", hint: "pi-ai-input-hint", counter: "pi-ai-input-counter" },
      guide: ASSISTANT_GUIDE,
      notConfiguredText: NOT_CONFIGURED_TEXT,
      pendingText: PENDING_TEXT,
      commentMax: ASSISTANT_LIMITS.comment,
      agentsRequest: blankRequest(),
      agentsView: null,
      configured: true,
      agent: session.agent || DEFAULT_AGENT,
      draft: session.draft,
      asking: false,
      pending: null,
      askError: null,
      owner: session.owner,
      session: session,
      origin: "",
      contextRemoved: false,
      featureName: "",
      companyName: "",
      feedback: {},
      announcement: "",
      restartNote: "",
      envelopeLabels: [],
      alive: true,
      retryUntil: 0,
      clock: 0,
      ticker: null,
      draftTimer: null,
      flash: NO_FLASH,
      flashTimer: null,
      target: null,
      ready: false,
      settled: false,
      prefilled: false,
      shown: false,
    };
  },
  computed: {
    view() {
      return this.agentsView || normalizeAgents(null);
    },
    agents() {
      return this.view.agents;
    },
    agentGroups() {
      return agentGroups(this.agents);
    },
    selectedAgent() {
      return agentByKey(this.agents, this.agent) || this.agents[0];
    },
    agentName() {
      return this.selectedAgent ? this.selectedAgent.label : "";
    },
    agentHelp() {
      return agentHelp(this.selectedAgent);
    },
    suggestions() {
      return this.selectedAgent ? this.selectedAgent.suggestions : [];
    },
    areaQuestions() {
      if (this.turns.length || this.pending || (this.selectedAgent && this.selectedAgent.group === "area")) return [];
      return areaSuggestions(this.agents);
    },
    maxLength() {
      return this.view.limits.maxQuestionLength;
    },
    costNotice() {
      return costNoticeText(this.view);
    },
    usageToday() {
      return usageTodayText(this.view);
    },
    ceilingReached() {
      return this.view.usageToday.ceilingReached === true;
    },
    context() {
      if (this.contextRemoved) return null;
      return contextFromRoute(this.$route && this.$route.query, this.origin, new Date());
    },
    queryCompanyName() {
      return contextCompanyName(this.$route && this.$route.query, this.context);
    },
    contextText() {
      return contextLabel(this.context, { featureName: this.featureName, companyName: this.queryCompanyName || this.companyName });
    },
    contextFeature() {
      return this.context && typeof this.context.featureType === "number" ? this.context.featureType : null;
    },
    contextCompany() {
      const context = this.context;
      return context && context.companyId && !this.queryCompanyName ? context.companyId + "/" + (context.shardCode || "") : "";
    },
    turns() {
      const conversationId = this.session.conversationId;
      const visited = this.session.visited;
      return this.session.turns.map((turn, index) => {
        const answer = normalizeAnswer(turn.answer);
        const anchor = chatTurnAnchor(turn, index);
        return {
          key: "t" + index + "-" + answer.interactionId,
          index: index,
          anchor: anchor,
          question: turn.question,
          answer: answer,
          feedback: turn.feedback,
          feedbackValue: turn.feedback ? turn.feedback.value : "",
          usageRoute: usageDetailRoute(answer.interactionId),
          linkQuery: chatLinkQuery(conversationId, chatTurnNumber(turn, index)),
          visited: visitedLinks(visited, anchor),
          exportContext: answerExportContext(turn.question, answer),
        };
      });
    },
    hiddenTurnsText() {
      return this.session.turns.length >= ASSISTANT_LIMITS.turnsShown
        ? "Mostrando as últimas " + ASSISTANT_LIMITS.turnsShown + " rodadas desta conversa; o assistente usa as 6 últimas como contexto."
        : "";
    },
    lastEnvelopes() {
      const last = this.turns[this.turns.length - 1];
      return last ? last.answer.envelopes : [];
    },
    inputDisabled() {
      return this.asking || !this.configured;
    },
    cooldownSeconds() {
      return this.retryUntil ? Math.max(0, Math.ceil((this.retryUntil - this.clock) / 1000)) : 0;
    },
    countdown() {
      return countdownText(this.cooldownSeconds);
    },
    sendBlocked() {
      return this.inputDisabled || this.cooldownSeconds > 0 || this.ceilingReached;
    },
    inputHint() {
      if (this.asking) return PENDING_TEXT;
      if (this.cooldownSeconds > 0) return "Envio liberado em " + this.countdown + ".";
      if (this.ceilingReached) return "Teto diário de custo atingido: novas perguntas voltam amanhã.";
      return "Enter envia; Shift+Enter quebra a linha.";
    },
    submitLabel() {
      if (this.asking) return "Consultando…";
      return this.cooldownSeconds > 0 ? "Aguarde " + this.countdown : "Perguntar";
    },
    draftReady() {
      return !questionError(this.draft, this.maxLength);
    },
    counterText() {
      return this.draft.length + "/" + this.maxLength;
    },
    placeholder() {
      return this.configured ? "Ex.: Quais empresas têm mais aderência usando o feedback?" : "Assistente não configurado neste ambiente.";
    },
  },
  watch: {
    contextFeature(value) {
      this.loadFeatureName(value);
    },
    contextCompany() {
      this.loadCompanyName();
    },
    agent(value) {
      updateChatState({ agent: value });
    },
    draft() {
      if (this.draftTimer) clearTimeout(this.draftTimer);
      this.draftTimer = setTimeout(this.saveDraft, DRAFT_DELAY_MS);
    },
  },
  created() {
    const query = (this.$route && this.$route.query) || {};
    this.target = chatTarget(this.session, query);
    if (isChatReturn(query, this.session.back)) this.contextRemoved = true;
    if (this.session.back) updateChatState({ back: null });
    if (chatOrigin(query)) this.replaceQuery(withoutChatQuery(query));
    this.loadAgents(false);
    this.loadFeatureName(this.contextFeature);
    this.loadCompanyName();
    const running = inflight[this.owner];
    if (running) this.follow(running);
  },
  mounted() {
    this.shown = true;
    this.settle();
  },
  beforeDestroy() {
    this.alive = false;
    if (this.ticker) clearInterval(this.ticker);
    this.ticker = null;
    if (this.flashTimer) clearTimeout(this.flashTimer);
    this.flashTimer = null;
    this.saveDraft();
  },
  methods: {
    replaceQuery(query) {
      if (this.$router) this.$router.replace({ query: query }, () => {}, () => {});
    },
    saveDraft() {
      if (this.draftTimer) clearTimeout(this.draftTimer);
      this.draftTimer = null;
      if (chatState().draft !== this.draft) updateChatState({ draft: this.draft });
    },
    saveScroll() {
      updateChatState({ scrollY: pageScroll() });
    },
    loadAgents(force) {
      const cached = force ? null : cachedAgents(this.owner);
      if (cached && !this.agentsRequest.loaded) {
        this.agentsRequest.envelope = cached;
        this.agentsRequest.loaded = true;
        this.agentsRequest.key = "agents";
        this.applyAgents(normalizeAgents(payloadOf(cached)));
        this.refreshAgents();
        return;
      }
      if (!shouldLoad(this.agentsRequest, "agents", force)) return;
      runRequest(this.agentsRequest, () => loadAssistantAgents(), agentsLoadError, "agents").then((applied) => {
        if (!applied) return;
        keepAgents(this.owner, this.agentsRequest.envelope);
        this.applyAgents(normalizeAgents(payloadOf(this.agentsRequest.envelope)));
      });
    },
    refreshAgents() {
      loadAssistantAgents().then((envelope) => {
        if (!this.alive || !this.agentsView) return;
        keepAgents(this.owner, envelope);
        this.applyAgents(normalizeAgents(payloadOf(envelope)));
      }, () => {});
    },
    applyAgents(view) {
      this.agentsView = view;
      this.configured = view.configured;
      if (!agentByKey(view.agents, this.agent)) this.agent = defaultAgentKey(view.agents);
      this.applyPrefill();
      this.ready = true;
      this.settle();
    },
    applyPrefill() {
      if (this.prefilled) return;
      this.prefilled = true;
      const query = (this.$route && this.$route.query) || {};
      if (!hasChatPrefill(query)) return;
      const prefill = chatPrefill(query, this.agents, this.maxLength);
      const fresh = !inflight[this.owner] && prefillStartsNewChat(this.session, prefill);
      if (fresh) {
        this.session = saveChatState(restartChat(Object.assign({}, chatState(), { agent: prefill.agent, draft: prefill.question || this.draft })));
        this.target = null;
        this.feedback = {};
        this.flash = NO_FLASH;
        this.restartNote = NEW_CHAT_FOR_PREFILL_TEXT;
        this.contextRemoved = false;
      }
      if (prefill.agent) this.agent = prefill.agent;
      if (prefill.question) this.draft = prefill.question;
      const notes = [];
      if (fresh) notes.push(NEW_CHAT_FOR_PREFILL_TEXT);
      if (prefill.question) notes.push("Pergunta preenchida: revise e envie quando quiser.");
      if (notes.length) this.announcement = notes.join(" ");
      this.replaceQuery(withoutChatQuery(withoutChatPrefill(query)));
      if (prefill.question) this.focusInput();
    },
    settle() {
      if (this.settled || !this.ready || !this.shown) return;
      this.settled = true;
      const target = this.target;
      const y = this.session.scrollY;
      this.$nextTick(() => {
        if (target) this.showTarget(target);
        else if (y > 0 && typeof window.scrollTo === "function") window.scrollTo(0, y);
      });
    },
    showTarget(target) {
      const list = this.$el && typeof this.$el.querySelectorAll === "function" ? this.$el.querySelectorAll(".pi-ai__turn") : [];
      const article = list[target.index];
      if (!article) return;
      const holder = target.link ? article.querySelector("[data-link-id=\"" + target.link + "\"]") : null;
      const focusable = holder && holder.tagName !== "A" ? holder.querySelector("a") : holder;
      if (focusable && typeof focusable.focus === "function") focusable.focus();
      const anchor = holder || article;
      if (typeof anchor.scrollIntoView === "function") anchor.scrollIntoView({ block: "center" });
      this.flashLink(target.anchor, holder ? target.link : "");
      this.announcement = "De volta à conversa, na pergunta " + (target.index + 1) + ".";
    },
    flashLink(anchor, link) {
      if (this.flashTimer) clearTimeout(this.flashTimer);
      this.flash = { anchor: anchor, link: link || "" };
      this.flashTimer = setTimeout(() => {
        this.flash = NO_FLASH;
        this.flashTimer = null;
      }, CHAT_FLASH_MS);
    },
    openLink(turn, link) {
      if (!link || !link.id) return;
      let next = withVisited(chatState(), visitedKey(turn.anchor, link.id));
      if (link.sameTab) next = Object.assign({}, next, { back: chatBack(next, turn.index, link.id), scrollY: pageScroll(), draft: this.draft });
      this.session = saveChatState(next);
    },
    loadFeatureName(featureType) {
      if (featureType === null || featureType === undefined) {
        this.featureName = "";
        return;
      }
      loadFeatureOptions().then((options) => {
        const found = (options || []).filter((option) => option && option.value === String(featureType))[0];
        if (this.contextFeature === featureType) this.featureName = found ? found.label : "";
      }, () => {
        this.featureName = "";
      });
    },
    loadCompanyName() {
      const key = this.contextCompany;
      const context = this.context;
      this.companyName = "";
      if (!key) return;
      searchCompanies(context.companyId).then((rows) => {
        if (this.alive && this.contextCompany === key) this.companyName = companyNameFrom(rows, context.companyId, context.shardCode);
      }, () => {});
    },
    onKeydown(event) {
      if (!event || event.key !== "Enter" || event.shiftKey || event.isComposing || event.keyCode === 229) return;
      event.preventDefault();
      this.submit();
    },
    submit() {
      this.ask(this.draft);
    },
    askAs(agent, question) {
      if (this.sendBlocked || !agentByKey(this.agents, agent)) return;
      this.agent = agent;
      this.ask(question);
    },
    ask(text) {
      if (this.sendBlocked) return;
      const problem = questionError(text, this.maxLength);
      if (problem) {
        this.askError = { kind: "invalid", message: problem, retryable: false };
        this.focusInput();
        return;
      }
      const question = cleanQuestion(text);
      const body = buildAskBody(question, this.agent, this.session.conversationId, this.context);
      const owner = this.owner;
      const request = askAssistant(body).then((answer) => {
        if (!answer || typeof answer !== "object" || !answer.answer || typeof answer.answer !== "object") {
          throw new Error("Resposta sem o corpo esperado.");
        }
        const latest = chatState();
        const session = latest.owner === owner ? saveChatState(Object.assign({}, latest, appendTurn(latest, question, answer))) : latest;
        return { answer: answer, session: session };
      });
      const entry = { question: question, request: request };
      const release = () => {
        if (inflight[owner] === entry) delete inflight[owner];
      };
      inflight[owner] = entry;
      request.then(release, release);
      this.askError = null;
      this.draft = "";
      this.saveDraft();
      this.announcement = "Pergunta enviada. " + PENDING_TEXT;
      this.follow(entry);
    },
    follow(entry) {
      this.asking = true;
      this.pending = { question: entry.question };
      this.scrollToEnd();
      entry.request.then((result) => {
        if (!this.alive) return;
        this.session = result.session;
        if (this.agentsView) this.agentsView = applyUsage(this.agentsView, result.answer);
        this.asking = false;
        this.pending = null;
        const headline = normalizeAnswer(result.answer).body.headline;
        this.announcement = "Resposta recebida." + (headline ? " " + headline : "");
        this.refreshAgents();
        this.scrollToEnd();
        this.focusInput();
      }, (error) => {
        if (!this.alive) return;
        const info = assistantError(error, this.view.limits);
        this.asking = false;
        this.pending = null;
        if (!this.draft) this.draft = entry.question;
        this.askError = info;
        if (info.kind === "not_configured") this.configured = false;
        if (info.retryAfterSeconds) this.startCooldown(info.retryAfterSeconds);
        if (LIMIT_KINDS.indexOf(info.kind) !== -1) this.refreshAgents();
        this.announcement = "";
        this.focusInput();
      });
    },
    startCooldown(seconds) {
      this.clock = Date.now();
      this.retryUntil = this.clock + Math.ceil(seconds) * 1000;
      if (!this.ticker) this.ticker = setInterval(this.tick, 1000);
    },
    tick() {
      this.clock = Date.now();
      if (this.retryUntil && this.clock >= this.retryUntil) this.endCooldown();
    },
    endCooldown() {
      if (this.ticker) clearInterval(this.ticker);
      this.ticker = null;
      this.retryUntil = 0;
      if (this.askError && LIMIT_KINDS.indexOf(this.askError.kind) !== -1) this.askError = null;
      this.announcement = "Você já pode perguntar de novo.";
      this.refreshAgents();
    },
    newConversation() {
      if (this.asking) return;
      this.session = saveChatState(restartChat(Object.assign({}, chatState(), { agent: this.agent, draft: this.draft })));
      if (!this.askError || LIMIT_KINDS.indexOf(this.askError.kind) === -1) this.askError = null;
      this.feedback = {};
      this.flash = NO_FLASH;
      this.restartNote = "";
      this.announcement = "Nova conversa iniciada.";
      this.focusInput();
    },
    removeContext() {
      this.contextRemoved = true;
      this.announcement = "Contexto removido: as próximas perguntas usam o período padrão.";
      this.focusInput();
    },
    focusInput() {
      this.$nextTick(() => {
        const input = this.$refs.input;
        if (input && !input.disabled && typeof input.focus === "function") input.focus();
      });
    },
    scrollToEnd() {
      this.$nextTick(() => {
        const list = this.$el && typeof this.$el.querySelectorAll === "function" ? this.$el.querySelectorAll(".pi-ai__turn") : [];
        const last = list.length ? list[list.length - 1] : null;
        if (last && typeof last.scrollIntoView === "function") last.scrollIntoView({ block: "nearest", behavior: "smooth" });
      });
    },
    feedbackKey(turn) {
      return "f" + turn.answer.interactionId;
    },
    feedbackOf(turn) {
      return this.feedback[this.feedbackKey(turn)] || BLANK_FEEDBACK;
    },
    patchFeedback(turn, changes) {
      this.$set(this.feedback, this.feedbackKey(turn), Object.assign({}, this.feedbackOf(turn), changes));
    },
    vote(turn, value) {
      if (this.feedbackOf(turn).busy || turn.feedbackValue === value) return;
      const comment = turn.feedback && turn.feedback.comment ? turn.feedback.comment : "";
      this.saveFeedback(turn, { value: value, comment: comment }, value === "not_useful" && !comment);
    },
    openComment(turn) {
      this.patchFeedback(turn, { commentOpen: true, comment: turn.feedback ? turn.feedback.comment : "", error: "" });
      this.$nextTick(() => {
        const box = this.$el && typeof this.$el.querySelector === "function" ? this.$el.querySelector("#pi-ai-fbc-" + turn.answer.interactionId) : null;
        if (box && typeof box.focus === "function") box.focus();
      });
    },
    closeComment(turn) {
      this.patchFeedback(turn, { commentOpen: false, error: "" });
    },
    setFeedbackComment(turn, value) {
      this.patchFeedback(turn, { comment: typeof value === "string" ? value.substring(0, ASSISTANT_LIMITS.comment) : "" });
    },
    sendComment(turn) {
      const state = this.feedbackOf(turn);
      if (state.busy || !turn.feedback) return;
      this.saveFeedback(turn, { value: turn.feedback.value, comment: (state.comment || "").trim() }, false);
    },
    saveFeedback(turn, chosen, openComment) {
      const id = turn.answer.interactionId;
      const owner = this.owner;
      const commenting = this.feedbackOf(turn).commentOpen;
      this.patchFeedback(turn, { busy: true, error: "" });
      sendAssistantFeedback(id, chosen.value === "useful", chosen.comment || null).then((result) => {
        const saved = feedbackFromResult(result, chosen);
        const latest = chatState();
        const session = latest.owner === owner ? saveChatState(Object.assign({}, latest, setTurnFeedback(latest, id, saved))) : latest;
        if (!this.alive) return;
        this.session = session;
        this.patchFeedback(turn, { busy: false, error: "", commentOpen: openComment, comment: saved ? saved.comment : "" });
        this.announcement = commenting ? "Comentário registrado." : "Avaliação registrada.";
      }).catch((error) => {
        if (this.alive) this.patchFeedback(turn, { busy: false, error: feedbackError(error) });
      });
    },
  },
};
</script>

<style src="../../components/pi/pi.css"></style>

<style scoped>
.pi-ai__guide {
  margin: 0 0 14px;
  padding: 10px 14px;
  border: 1px solid #e3e8f0;
  border-radius: 6px;
  background: #fafbfc;
  font-size: 12px;
  color: #3a424e;
}

.pi-ai__guide-title {
  font-weight: 700;
  cursor: pointer;
}

.pi-ai__guide-list {
  margin: 8px 0 0;
  padding-left: 18px;
  line-height: 1.5;
}

.pi-ai__notice {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0 0 14px;
  padding: 14px 16px;
  border: 1px solid #f2d9a6;
  border-radius: 6px;
  background: #fffbf2;
  font-size: 13px;
  color: #4e5560;
}

.pi-ai__notice-title {
  color: #7a4f00;
}

.pi-ai__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px 14px;
  margin-bottom: 4px;
}

.pi-ai__agent .form-control {
  width: 300px;
  max-width: 100%;
}

.pi-ai__agent-label {
  display: inline-flex;
  align-items: center;
}

.pi-ai__info {
  margin-left: 4px;
  vertical-align: middle;
}

.pi-ai__agent-help {
  min-height: 16px;
  margin-bottom: 10px;
}

.pi-ai__expertise {
  margin: 0 0 2px;
  font-size: 12px;
  color: #3a424e;
}

.pi-ai__agent-description {
  margin: 0;
}

.pi-ai__areas {
  margin: -4px 0 16px;
}

.pi-ai__areas-title {
  display: flex;
  align-items: center;
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 700;
  color: #3a424e;
}

.pi-ai__area {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.pi-ai__area-label {
  flex: 0 0 210px;
  max-width: 100%;
  font-size: 12px;
  font-weight: 600;
  color: #4e5560;
}

.pi-ai__turn.is-flash .pi-ai__answer {
  border-color: #f2c94c;
  box-shadow: 0 0 0 3px #fff1b8;
  transition: box-shadow 0.3s ease, border-color 0.3s ease;
}

@media (prefers-reduced-motion: reduce) {
  .pi-ai__turn.is-flash .pi-ai__answer {
    transition: none;
  }
}

.pi-ai__context {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  height: 32px;
  padding: 0 4px 0 10px;
  border: 1px solid #c9d6fb;
  border-radius: 16px;
  background: #eef3fe;
  color: #1a45b8;
  font-size: 12px;
  font-weight: 600;
}

.pi-ai__context-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pi-ai__context-remove {
  width: 24px;
  height: 24px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #1a45b8;
  font-size: 16px;
  line-height: 24px;
  cursor: pointer;
}

.pi-ai__context-remove:hover {
  background: #dce6fd;
}

.pi-ai__cost {
  margin: 0 0 2px;
  font-size: 12px;
  color: #3a424e;
}

.pi-ai__today {
  margin-bottom: 12px;
}

.pi-ai__suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 16px;
}

.pi-ai__suggestion {
  height: auto;
  min-height: 32px;
  max-width: 100%;
  padding: 6px 12px;
  line-height: 1.35;
  text-align: left;
  white-space: normal;
}

.pi-ai__conversation {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
}

.pi-ai__turn {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pi-ai__question {
  align-self: flex-end;
  max-width: 80%;
  padding: 10px 14px;
  border-radius: 12px 12px 2px 12px;
  background: #1b59f8;
  color: #ffffff;
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-line;
  word-break: break-word;
}

.pi-ai__answer {
  max-width: 100%;
  padding: 14px 16px;
  border: 1px solid #e3e8f0;
  border-radius: 2px 12px 12px 12px;
  background: #ffffff;
}

.pi-ai__waiting {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #4e5560;
}

.pi-ai__dots {
  display: inline-flex;
  gap: 4px;
}

.pi-ai__dots span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #9aa1ab;
  animation: pi-ai-dot 1.2s infinite ease-in-out;
}

.pi-ai__dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.pi-ai__dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes pi-ai-dot {
  0%, 80%, 100% {
    opacity: 0.3;
  }
  40% {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pi-ai__dots span {
    animation: none;
  }
}

.pi-ai__footer {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 12px;
  margin: 12px 0 0;
  padding-top: 8px;
  border-top: 1px solid #eef0f2;
  font-size: 11px;
  color: #6b7280;
}

.pi-ai__footer-link {
  font-size: 11px;
}

.pi-ai__feedback {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  margin-top: 8px;
  font-size: 12px;
}

.pi-ai__feedback-label {
  color: #4e5560;
  font-weight: 600;
}

.pi-ai__feedback-choices {
  display: inline-flex;
  border: 1px solid #c5cad3;
  border-radius: 4px;
  overflow: hidden;
}

.pi-ai__feedback-btn {
  height: 28px;
}

.pi-ai__feedback-comment {
  color: #4e5560;
  font-style: italic;
}

.pi-ai__feedback-edit {
  padding: 0;
  border: 0;
  background: none;
  color: #1b59f8;
  font-size: 12px;
  cursor: pointer;
}

.pi-ai__feedback-form {
  display: flex;
  flex-basis: 100%;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px;
}

.pi-ai__feedback-field {
  display: flex;
  flex: 1 1 320px;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  font-weight: 400;
  color: #4e5560;
}

.pi-ai__feedback-box {
  height: auto;
  resize: vertical;
}

.pi-ai__feedback-actions {
  display: flex;
  gap: 6px;
}

.pi-ai__feedback-error {
  flex-basis: 100%;
  margin: 0;
  color: #a3172b;
}

.pi-ai__unsaved {
  margin-top: 8px;
}

.pi-ai__error {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin: 0 0 10px;
  padding: 10px 14px;
  border: 1px solid #f3c2c9;
  border-radius: 6px;
  background: #fff7f8;
  font-size: 13px;
  color: #a3172b;
}

.pi-ai__countdown {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.pi-ai__error--busy,
.pi-ai__error--rate_limited,
.pi-ai__error--daily_budget,
.pi-ai__error--invalid,
.pi-ai__error--revenue_denied,
.pi-ai__error--not_configured {
  border-color: #f2d9a6;
  background: #fffbf2;
  color: #7a4f00;
}

.pi-ai__input {
  position: sticky;
  bottom: 0;
  padding: 10px 0 4px;
  background: #ffffff;
}

.pi-ai__input-label {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 700;
  color: #3a424e;
}

.pi-ai__input-box {
  height: auto;
  min-height: 72px;
  font-size: 13px;
  resize: vertical;
}

.pi-ai__input-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 6px 14px;
  margin-top: 6px;
}

.pi-ai__input-bar > :first-child {
  margin-right: auto;
}

.pi-ai__counter {
  font-variant-numeric: tabular-nums;
}

@media (max-width: 767px) {
  .pi-ai__question {
    max-width: 100%;
  }

  .pi-ai__agent .form-control {
    width: 100%;
  }

  .pi-ai__area-label {
    flex-basis: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/productIntelligence/piAssistant.vue